import React, { useMemo } from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import useModalOutsideClick from '../../hooks/useModalOutsideClick';
import useSortedData from '../../hooks/useSortedData';
import './ViewGolfLeaderboardModal.css';

const ViewGolfLeaderboardModal = ({
  show,
  onClose,
  players,
  userPlayerNames
}) => {
  // Add the useModalOutsideClick hook to close when clicking outside
  useModalOutsideClick(show, onClose, "view-golf-leaderboard-overlay");

  // Helper function to get ordinal suffix
  const getOrdinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Create leaderboard rows with position and player info
  const leaderboardRows = useMemo(() => {
    if (!players) return [];
    // First sort players by score (ascending for golf - lower scores first)
    const sortedPlayers = [...players].sort((a, b) => {
      const scoreA = a.sportSpecificData?.score || 0;
      const scoreB = b.sportSpecificData?.score || 0;
      return scoreA - scoreB;
    });

    // Group players by score to handle ties
    const scoreGroups = sortedPlayers.reduce((groups, player) => {
      const score = player.sportSpecificData?.score || 0;
      if (!groups[score]) {
        groups[score] = [];
      }
      groups[score].push(player);
      return groups;
    }, {});

    // Create rows with proper position handling for ties
    let currentPosition = 1;
    const rows = [];

    // Sort score groups by score (ascending) before creating rows
    Object.entries(scoreGroups)
      .sort(([scoreA], [scoreB]) => Number(scoreA) - Number(scoreB))
      .forEach(([score, players]) => {
        const isTie = players.length > 1;
        const position = currentPosition;

        players.forEach(player => {
          const thru = !player.sportSpecificData?.thru || player.sportSpecificData.thru === '0' ? '-' : player.sportSpecificData.thru;
          const displayScore = score === '0' ? 'E' : parseInt(score) > 0 ? `+${score}` : score;
          const displayPosition = isTie ? `T${getOrdinalSuffix(position)}` : getOrdinalSuffix(position);

          rows.push({
            id: `${player.name}-${score}`,
            place: displayPosition,
            player: player.name,
            thru: thru,
            score: displayScore,
            rawScore: Number(score), // for sorting
          });
        });

        currentPosition += players.length;
      });

    return rows;
  }, [players]);

  // Sorting logic using useSortedData
  const { sortedData, requestSort, sortConfig } = useSortedData(
    leaderboardRows,
    'rawScore', // initial sort key (lowest score first)
    'ascending'
  );

  // Helper to display sort indicators in the table header
  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  if (!show) return null;

  return (
    <div className="view-golf-leaderboard-overlay">
      <div className="view-golf-leaderboard-dialog">
        <div className="view-golf-leaderboard-header">
          <h3>Golf Leaderboard</h3>
          <button className="view-golf-leaderboard-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="view-golf-leaderboard-content">
          {sortedData.length > 0 ? (
            <table className="golf-leaderboard-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('place')} style={{ cursor: 'pointer' }}>PLACE{getSortIndicator('place')}</th>
                  <th onClick={() => requestSort('player')} style={{ cursor: 'pointer' }}>PLAYER{getSortIndicator('player')}</th>
                  <th onClick={() => requestSort('thru')} style={{ cursor: 'pointer' }}>THRU{getSortIndicator('thru')}</th>
                  <th onClick={() => requestSort('rawScore')} style={{ cursor: 'pointer' }}>SCORE{getSortIndicator('rawScore')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row) => (
                  <tr key={row.id}>
                    <td className="place">{row.place}</td>
                    <td className="player">
                      {userPlayerNames.includes(row.player.toLowerCase()) && '⭐ '}
                      {row.player}
                    </td>
                    <td className="thru">{row.thru}</td>
                    <td className="score">{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-leaderboard-message">No leaderboard information available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

ViewGolfLeaderboardModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      sportSpecificData: PropTypes.shape({
        thru: PropTypes.string,
        score: PropTypes.number
      })
    })
  ).isRequired,
  userPlayerNames: PropTypes.arrayOf(PropTypes.string)
};

ViewGolfLeaderboardModal.defaultProps = {
  userPlayerNames: []
};

export default ViewGolfLeaderboardModal; 